import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Tag, Pencil, Save, X, Plus, Trash2, Package } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { useToast } from '@/hooks/use-toast';
import { dataService } from '@/services/data-service';
import type { Category, SubCategory, AssetOptions, DepreciationMethod, DepreciationFrequency } from '@/types/settings';

// Load default depreciation settings from Asset Options (stored in localStorage)
function getDefaultDepreciationSettings(): {
  defaultMethod: DepreciationMethod;
  defaultFrequency: DepreciationFrequency;
  defaultPercentage: number;
  defaultPeriodYears: number;
  doNotDepreciate: boolean;
} {
  try {
    const savedOptionsStr = localStorage.getItem('assetOptions');
    if (savedOptionsStr) {
      const assetOptions: AssetOptions = JSON.parse(savedOptionsStr);
      if (assetOptions.defaultSettings) {
        return {
          defaultMethod: assetOptions.defaultSettings.defaultMethod,
          defaultFrequency: assetOptions.defaultSettings.defaultFrequency,
          defaultPercentage: assetOptions.defaultSettings.defaultPercentage ?? 0,
          defaultPeriodYears: assetOptions.defaultSettings.defaultPeriodYears ?? 0,
          doNotDepreciate: assetOptions.defaultSettings.doNotDepreciate ?? false,
        };
      }
    }
  } catch (error) {
    console.error('Failed to load default depreciation settings from Asset Options:', error);
  }
  
  // Fallback to hardcoded defaults if Asset Options not available
  return {
    defaultMethod: 'Straight Line',
    defaultFrequency: 'Monthly',
    defaultPercentage: 0,
    defaultPeriodYears: 0,
    doNotDepreciate: false,
  };
}

// Helper function to create default category data - extracted to reduce cognitive complexity (S3776)
function createDefaultCategoryData(): Partial<Category> {
  const depreciationDefaults = getDefaultDepreciationSettings();
  
  return {
    code: '',
    description: '',
    defaultMethod: depreciationDefaults.defaultMethod,
    defaultFrequency: depreciationDefaults.defaultFrequency,
    defaultPercentage: depreciationDefaults.defaultPercentage,
    defaultPeriodYears: depreciationDefaults.defaultPeriodYears,
    doNotDepreciate: depreciationDefaults.doNotDepreciate,
    userField1: '',
    userField2: '',
    userField3: '',
  };
}

// Category detail page - updated to use correct API functions
export default function CategoryDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isNew = id === 'new';

  const [category, setCategory] = useState<Category | null>(null);
  const [editedCategory, setEditedCategory] = useState<Partial<Category>>({});
  const [isEditMode, setIsEditMode] = useState(isNew);
  const [loading, setLoading] = useState(!isNew);
  const [error, setError] = useState<string | null>(null);

  // Sub-category state
  const [showSubCategoryDialog, setShowSubCategoryDialog] = useState(false);
  const [editingSubCategory, setEditingSubCategory] = useState<SubCategory | null>(null);
  const [subCategoryFormData, setSubCategoryFormData] = useState<Partial<SubCategory>>({
    name: '',
    notes: '',
  });

  // Delete dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    if (!isNew && id) {
      loadCategory(id);
    } else {
      // Use extracted helper function to reduce cognitive complexity (S3776)
      setEditedCategory(createDefaultCategoryData());
    }
  }, [id, isNew]);

  const loadCategory = async (categoryId: string) => {
    setLoading(true);
    setError(null);
    try {
      const categoryData = await dataService.getCategory(categoryId);
      
      // Load sub-categories
      const subCategories = await dataService.getSubCategoriesByParentId(categoryId);
      
      const fullCategory = {
        ...categoryData,
        subCategories: subCategories ?? [],
      };

      setCategory(fullCategory);
      setEditedCategory(fullCategory);
    } catch (err) {
      setError('Failed to load category. Please try again.');
      console.error('Error loading category:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      if (isNew) {
        // Create new category
                        const newCategory: Category = {
                          id: Date.now().toString(),
                          code: editedCategory.code ?? '',
                          description: editedCategory.description ?? '',
                          defaultMethod: editedCategory.defaultMethod ?? 'Straight Line',
                          defaultFrequency: editedCategory.defaultFrequency ?? 'Monthly',
                          defaultPercentage: editedCategory.defaultPercentage ?? 0,
                          defaultPeriodYears: editedCategory.defaultPeriodYears ?? 0,
                          doNotDepreciate: editedCategory.doNotDepreciate ?? false,
                          userField1: editedCategory.userField1 ?? '',
                          userField2: editedCategory.userField2 ?? '',
                          userField3: editedCategory.userField3 ?? '',
                          managementReportColumn: editedCategory.managementReportColumn,
                  assetCount: 0,
                  createdBy: 'System',
                  createdDate: new Date().toISOString(),
                  subCategories: [],
                };
        await dataService.createCategory(newCategory);
        toast({
          title: 'Success',
          description: 'Category created successfully',
        });
        navigate('/administration/categories');
      } else if (id) {
        // Update existing category
        await dataService.updateCategory(id, editedCategory as Category);
        toast({
          title: 'Success',
          description: 'Category updated successfully',
        });
        setCategory({ ...category!, ...editedCategory });
        setIsEditMode(false);
      }
    } catch (err) {
      console.error('Error saving category:', err);
      toast({
        title: 'Error',
        description: 'Failed to save category. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleCancel = () => {
    if (isNew) {
      navigate('/administration/categories');
    } else {
      setEditedCategory(category || {});
      setIsEditMode(false);
    }
  };

  const handleOpenSubCategoryDialog = (subCategory?: SubCategory) => {
    if (subCategory) {
      setEditingSubCategory(subCategory);
      setSubCategoryFormData({
        name: subCategory.name,
        notes: subCategory.notes,
      });
    } else {
      setEditingSubCategory(null);
      setSubCategoryFormData({
        name: '',
        notes: '',
      });
    }
    setShowSubCategoryDialog(true);
  };

  const handleSaveSubCategory = async () => {
    if (!subCategoryFormData.name?.trim()) return;

    try {
      if (editingSubCategory) {
        // Update existing sub-category
        const updatedSubCategory: SubCategory = {
          ...editingSubCategory,
          ...subCategoryFormData,
        } as SubCategory;
        await dataService.updateSubCategory(editingSubCategory.id, updatedSubCategory);
        
        const updatedSubCategories = category?.subCategories?.map(sc =>
          sc.id === editingSubCategory.id
            ? updatedSubCategory
            : sc
        );
        setCategory({ ...category!, subCategories: updatedSubCategories });
        
        toast({
          title: 'Success',
          description: 'Sub-category updated successfully',
        });
      } else {
        // Add new sub-category
        const newSubCategory: SubCategory = {
          id: Date.now().toString(),
          name: subCategoryFormData.name,
          notes: subCategoryFormData.notes ?? '',
          parentCategoryId: category?.id ?? '',
          assetCount: 0,
        };

        const createdSubCategory = await dataService.createSubCategory(newSubCategory);
        const updatedSubCategories = [...(category?.subCategories ?? []), createdSubCategory];
        setCategory({ ...category!, subCategories: updatedSubCategories });
        
        toast({
          title: 'Success',
          description: 'Sub-category created successfully',
        });
      }
      setShowSubCategoryDialog(false);
    } catch (error) {
      console.error('Failed to save sub-category:', error);
      toast({
        title: 'Error',
        description: 'Failed to save sub-category. Please try again.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteSubCategory = async (subCategoryId: string) => {
    try {
      await dataService.deleteSubCategory(subCategoryId);
      const updatedSubCategories = category?.subCategories?.filter(sc => sc.id !== subCategoryId);
      setCategory({ ...category!, subCategories: updatedSubCategories });
      
      toast({
        title: 'Success',
        description: 'Sub-category deleted successfully',
      });
    } catch (error: any) {
      console.error('Failed to delete sub-category:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete sub-category. Please try again.';
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    console.log('[CategoryDetail] handleDelete called for category:', id);
    try {
      if (id) {
        console.log('[CategoryDetail] Calling dataService.deleteCategory...');
        await dataService.deleteCategory(id);
        console.log('[CategoryDetail] Delete successful');
        toast({
          title: 'Success',
          description: 'Category deleted successfully',
        });
        navigate('/administration/categories');
      }
    } catch (error: any) {
      console.error('[CategoryDetail] Failed to delete category:', error);
      const errorMessage = error?.response?.data?.message || error?.message || 'Failed to delete category. Please try again.';
      console.log('[CategoryDetail] Error message:', errorMessage);
      toast({
        title: 'Error',
        description: errorMessage,
        variant: 'destructive',
      });
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium">Loading category...</div>
        </div>
      </div>
    );
  }

  if (error && !isNew) {
    return (
      <div className="flex h-full items-center justify-center">
        <div className="text-center">
          <div className="text-lg font-medium text-destructive">{error}</div>
          <Button onClick={() => navigate('/administration/categories')} className="mt-4">
            Back to Categories
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b bg-card p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/administration/categories')}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <Tag className="h-6 w-6 text-primary" />
            <div>
              <h1 className="text-2xl font-bold">
                {isNew ? 'New Category' : (editedCategory.code ?? 'Category')}
              </h1>
              <p className="text-sm text-muted-foreground">
                {isNew ? 'Create a new category' : 'View and edit category details'}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!isNew && !isEditMode && (
              <>
                <Button variant="outline" size="sm" onClick={() => setIsEditMode(true)}>
                  <Pencil className="mr-2 h-4 w-4" />
                  Edit
                </Button>
                <Button variant="outline" size="sm" onClick={() => {
                  console.log('[CategoryDetail] Delete button clicked');
                  setShowDeleteDialog(true);
                }}>
                  <Trash2 className="mr-2 h-4 w-4" />
                  Delete
                </Button>
              </>
            )}
            {(isNew || isEditMode) && (
              <>
                <Button variant="outline" size="sm" onClick={handleCancel}>
                  <X className="mr-2 h-4 w-4" />
                  Cancel
                </Button>
                <Button size="sm" onClick={handleSave}>
                  <Save className="mr-2 h-4 w-4" />
                  {isNew ? 'Create Category' : 'Save Changes'}
                </Button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <div className="space-y-6">
          {/* Category Details Card */}
          <Card className="border">
            <CardHeader>
              <CardTitle>Category Details</CardTitle>
              <CardDescription>Basic category information and depreciation settings</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Basic Information */}
              <div>
                <h3 className="mb-4 text-sm font-semibold text-muted-foreground">Basic Information</h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="code">
                      Category Code <span className="text-destructive">*</span>
                    </Label>
                    <Input
                      id="code"
                      value={editedCategory.code ?? ''}
                      onChange={(e) =>
                        setEditedCategory({ ...editedCategory, code: e.target.value.toUpperCase() })
                      }
                      disabled={!isEditMode}
                      className={!isEditMode ? 'bg-muted' : ''}
                      required
                    />
                  </div>
                  <div className="space-y-2 md:col-span-2">
                    <Label htmlFor="description">Description</Label>
                    <Input
                      id="description"
                      value={editedCategory.description ?? ''}
                      onChange={(e) =>
                        setEditedCategory({ ...editedCategory, description: e.target.value })
                      }
                      disabled={!isEditMode}
                      className={!isEditMode ? 'bg-muted' : ''}
                    />
                  </div>
                </div>
              </div>

              {/* Depreciation Defaults */}
              <div>
                <h3 className="mb-4 text-sm font-semibold text-muted-foreground">Depreciation Defaults</h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="defaultMethod">Default Method</Label>
                    {isEditMode ? (
                      <Select
                        value={editedCategory.defaultMethod ?? 'Straight Line'}
                        onValueChange={(value: string) =>
                          setEditedCategory({ ...editedCategory, defaultMethod: value as Category['defaultMethod'] })
                        }
                      >
                        <SelectTrigger id="defaultMethod">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Straight Line">Straight Line</SelectItem>
                          <SelectItem value="Reducing Balance">Reducing Balance</SelectItem>
                          <SelectItem value="Sum of Digits">Sum of Digits</SelectItem>
                          <SelectItem value="Period Ratio">Period Ratio</SelectItem>
                          <SelectItem value="None">None</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={editedCategory.defaultMethod ?? ''}
                        disabled
                        className="bg-muted"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="defaultFrequency">Default Frequency</Label>
                    {isEditMode ? (
                      <Select
                        value={editedCategory.defaultFrequency ?? 'Monthly'}
                        onValueChange={(value: string) =>
                          setEditedCategory({ ...editedCategory, defaultFrequency: value as Category['defaultFrequency'] })
                        }
                      >
                        <SelectTrigger id="defaultFrequency">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Monthly">Monthly</SelectItem>
                          <SelectItem value="Quarterly">Quarterly</SelectItem>
                          <SelectItem value="Half Yearly">Half Yearly</SelectItem>
                          <SelectItem value="Yearly">Yearly</SelectItem>
                          <SelectItem value="N/A">N/A</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={editedCategory.defaultFrequency ?? ''}
                        disabled
                        className="bg-muted"
                      />
                    )}
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="defaultPercentage">Default Percentage</Label>
                    <Input
                      id="defaultPercentage"
                      type="number"
                      step="0.001"
                      value={editedCategory.defaultPercentage ?? 0}
                      onChange={(e) =>
                        setEditedCategory({
                          ...editedCategory,
                          defaultPercentage: parseFloat(e.target.value) ?? 0,
                        })
                      }
                      disabled={!isEditMode}
                      className={!isEditMode ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="defaultPeriodYears">Default Period (Years)</Label>
                    <Input
                      id="defaultPeriodYears"
                      type="number"
                      step="0.001"
                      value={editedCategory.defaultPeriodYears ?? 0}
                      onChange={(e) =>
                        setEditedCategory({
                          ...editedCategory,
                          defaultPeriodYears: parseFloat(e.target.value) ?? 0,
                        })
                      }
                      disabled={!isEditMode}
                      className={!isEditMode ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="flex items-center space-x-2">
                    <Switch
                      id="doNotDepreciate"
                      checked={editedCategory.doNotDepreciate || false}
                      onCheckedChange={(checked) =>
                        setEditedCategory({ ...editedCategory, doNotDepreciate: checked })
                      }
                      disabled={!isEditMode}
                    />
                    <Label htmlFor="doNotDepreciate">Do Not Depreciate</Label>
                  </div>
                </div>
              </div>

              {/* Management Reporting */}
              <div>
                <h3 className="mb-4 text-sm font-semibold text-muted-foreground">Management Reporting</h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="managementReportColumn">Management Report Column</Label>
                    {isEditMode ? (
                      <Select
                        value={editedCategory.managementReportColumn?.toString() ?? 'none'}
                        onValueChange={(value: string) =>
                          setEditedCategory({
                            ...editedCategory,
                            managementReportColumn: value === 'none' ? undefined : parseInt(value),
                          })
                        }
                      >
                        <SelectTrigger id="managementReportColumn">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((col) => (
                            <SelectItem key={col} value={col.toString()}>Column {col}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <Input
                        value={editedCategory.managementReportColumn ? `Column ${editedCategory.managementReportColumn}` : 'None'}
                        disabled
                        className="bg-muted"
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Custom Fields */}
              <div>
                <h3 className="mb-4 text-sm font-semibold text-muted-foreground">Custom Fields</h3>
                <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                  <div className="space-y-2">
                    <Label htmlFor="userField1">User Field 1</Label>
                    <Input
                      id="userField1"
                      value={editedCategory.userField1 ?? ''}
                      onChange={(e) =>
                        setEditedCategory({ ...editedCategory, userField1: e.target.value })
                      }
                      disabled={!isEditMode}
                      className={!isEditMode ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="userField2">User Field 2</Label>
                    <Input
                      id="userField2"
                      value={editedCategory.userField2 ?? ''}
                      onChange={(e) =>
                        setEditedCategory({ ...editedCategory, userField2: e.target.value })
                      }
                      disabled={!isEditMode}
                      className={!isEditMode ? 'bg-muted' : ''}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="userField3">User Field 3</Label>
                    <Input
                      id="userField3"
                      value={editedCategory.userField3 ?? ''}
                      onChange={(e) =>
                        setEditedCategory({ ...editedCategory, userField3: e.target.value })
                      }
                      disabled={!isEditMode}
                      className={!isEditMode ? 'bg-muted' : ''}
                    />
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sub-Categories Card - Only show for existing categories */}
          {!isNew && category && (
            <Card className="border">
              <CardHeader className="pb-[15px]">
                <div className="flex items-center justify-between">
                  <CardTitle>Sub-Categories</CardTitle>
                  <Button size="sm" onClick={() => handleOpenSubCategoryDialog()}>
                    <Plus className="h-4 w-4 mr-2" />
                    Add Sub-Category
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="p-0 border-t">
                {category.subCategories && category.subCategories.length > 0 ? (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="pl-6">Name</TableHead>
                        <TableHead>Notes</TableHead>
                        <TableHead className="text-center">Assets</TableHead>
                        <TableHead className="text-right pr-6">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {category.subCategories.map((subCategory) => (
                        <TableRow key={subCategory.id}>
                          <TableCell className="font-medium pl-6">{subCategory.name}</TableCell>
                          <TableCell className="max-w-xs truncate">{subCategory.notes ?? '-'}</TableCell>
                          <TableCell className="text-center">
                            <button
                              className="inline-flex items-center gap-1 text-primary hover:underline cursor-pointer"
                            >
                              <Package className="h-3 w-3" />
                              <Badge variant="secondary">{subCategory.assetCount ?? 0}</Badge>
                            </button>
                          </TableCell>
                          <TableCell className="text-right pr-6">
                            <div className="flex items-center justify-end gap-2">
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => handleOpenSubCategoryDialog(subCategory)}
                              >
                                <Pencil className="h-4 w-4" />
                              </Button>
                              <Button 
                                variant="ghost" 
                                size="sm"
                                onClick={() => {
                                  if (confirm('Are you sure you want to delete this sub-category?')) {
                                    handleDeleteSubCategory(subCategory.id);
                                  }
                                }}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                ) : (
                  <div className="text-center py-12 text-muted-foreground">
                    <Tag className="h-12 w-12 mx-auto mb-4 opacity-50" />
                    <p className="font-medium">No sub-categories yet</p>
                    <p className="text-sm mt-1">Click "Add Sub-Category" to create one</p>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Category</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete this category? This will also delete all sub-categories. This action
              cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              console.log('[CategoryDetail] AlertDialog Delete button clicked');
              handleDelete();
            }} className="bg-destructive text-destructive-foreground">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Sub-Category Dialog */}
      <Dialog open={showSubCategoryDialog} onOpenChange={setShowSubCategoryDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingSubCategory ? 'Edit Sub-Category' : 'Add Sub-Category'}</DialogTitle>
            <DialogDescription>
              {editingSubCategory
                ? 'Update the sub-category details below.'
                : `Add a new sub-category to ${category?.code ?? 'this category'}.`}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="subCategoryName">Sub-Category Name *</Label>
              <Input
                id="subCategoryName"
                value={subCategoryFormData.name ?? ''}
                onChange={(e) => setSubCategoryFormData({ ...subCategoryFormData, name: e.target.value })}
                placeholder="e.g., Laptops, Desktops"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="subCategoryNotes">Notes</Label>
              <Textarea
                id="subCategoryNotes"
                value={subCategoryFormData.notes ?? ''}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setSubCategoryFormData({ ...subCategoryFormData, notes: e.target.value })}
                placeholder="Additional notes about this sub-category"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSubCategoryDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveSubCategory} disabled={!subCategoryFormData.name}>
              {editingSubCategory ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
